import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import mcApi, {
  isActiveRun,
  MC_DEFAULT_TARGET,
  type McEvent,
  type McIssue,
  type McIssueStatus,
  type McMemoryEntry,
  type McPlaybook,
  type McPlaybookDetail,
  type McPlaybookFields,
  type McRun,
  type McRunDetail,
  type McSettingsFields,
  type StartMcRunPayload,
} from "../lib/mc";

export const MC_POLL_INTERVAL_MS = 10_000;
const ARTIFACT_URL_TTL_MS = 1000 * 60 * 50;
const TARGETS_STALE_MS = 1000 * 60 * 10;

export const mcKeys = {
  all: ["mc"] as const,
  targets: () => [...mcKeys.all, "targets"] as const,
  playbooks: (targetId: string) =>
    [...mcKeys.all, "targets", targetId, "playbooks"] as const,
  runs: () => [...mcKeys.all, "runs"] as const,
  run: (runId: string) => [...mcKeys.all, "run", runId] as const,
  events: (runId: string) => [...mcKeys.all, "run", runId, "events"] as const,
  usage: (runId: string) => [...mcKeys.all, "run", runId, "usage"] as const,
  artifactUrl: (runId: string, key: string) =>
    [...mcKeys.all, "run", runId, "artifactUrl", key] as const,
  settings: (targetId: string) =>
    [...mcKeys.all, "targets", targetId, "settings"] as const,
  revisions: (targetId: string, playbookId: string) =>
    [...mcKeys.playbooks(targetId), playbookId, "revisions"] as const,
  memory: () => [...mcKeys.all, "memory"] as const,
  issues: () => [...mcKeys.all, "issues"] as const,
  issue: (issueId: number) => [...mcKeys.all, "issues", issueId] as const,
};

/** Retries transient failures, but not a 404 from an MC API that doesn't have the endpoint (or the run). */
const retryUnless404 = (
  failureCount: number,
  error: Error & { status?: number },
) => error.status !== 404 && failureCount < 3;

/** Recent runs, polled so new and changing runs show up without a refresh. */
export function useMcRunsQuery() {
  return useQuery({
    queryKey: mcKeys.runs(),
    queryFn: () => mcApi.getRuns(),
    select: (data): McRun[] => data?.runs ?? [],
    staleTime: 0,
    refetchInterval: MC_POLL_INTERVAL_MS,
  });
}

/** The playbooks runs can be started with, the default first. Empty while loading. */
export function useMcTargetPlaybooksQuery(targetId = MC_DEFAULT_TARGET) {
  return useQuery({
    queryKey: mcKeys.targets(),
    queryFn: () => mcApi.getTargets(),
    select: (data): McPlaybook[] =>
      data?.targets?.find((target) => target.id === targetId)?.playbooks ?? [],
    staleTime: TARGETS_STALE_MS,
    retry: retryUnless404,
  });
}

/** The target's playbooks with their text and parsed checklists, the default first. */
export function useMcPlaybooksQuery(targetId = MC_DEFAULT_TARGET) {
  return useQuery({
    queryKey: mcKeys.playbooks(targetId),
    queryFn: () => mcApi.getPlaybooks(targetId),
    select: (data): McPlaybookDetail[] => data?.playbooks ?? [],
    staleTime: 0,
    retry: retryUnless404,
  });
}

export function useMcPlaybookRevisionsQuery(
  playbookId: string,
  { enabled = true, targetId = MC_DEFAULT_TARGET } = {},
) {
  return useQuery({
    queryKey: mcKeys.revisions(targetId, playbookId),
    queryFn: () => mcApi.getPlaybookRevisions(targetId, playbookId),
    select: (data) => data?.revisions ?? [],
    enabled: enabled && !!playbookId,
    staleTime: 0,
  });
}

/** Playbook edits change both the playbooks page and the run modal's list (which comes from /targets). */
const invalidatePlaybooks = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: [...mcKeys.all, "targets"] });

export function useCreateMcPlaybook(targetId = MC_DEFAULT_TARGET) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fields: McPlaybookFields) =>
      mcApi.createPlaybook(targetId, fields),
    onSuccess: () => invalidatePlaybooks(queryClient),
  });
}

export function useUpdateMcPlaybook(targetId = MC_DEFAULT_TARGET) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...patch
    }: Partial<McPlaybookFields> & { id: string; default?: true }) =>
      mcApi.updatePlaybook(targetId, id, patch),
    onSuccess: () => invalidatePlaybooks(queryClient),
  });
}

export function useDeleteMcPlaybook(targetId = MC_DEFAULT_TARGET) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (playbookId: string) =>
      mcApi.deletePlaybook(targetId, playbookId),
    onSuccess: () => invalidatePlaybooks(queryClient),
  });
}

export function useMcSettingsQuery(targetId = MC_DEFAULT_TARGET) {
  return useQuery({
    queryKey: mcKeys.settings(targetId),
    queryFn: () => mcApi.getSettings(targetId),
    select: (data) => data.settings,
    staleTime: 0,
    retry: retryUnless404,
  });
}

export function useUpdateMcSettings(targetId = MC_DEFAULT_TARGET) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fields: Partial<McSettingsFields>) =>
      mcApi.updateSettings(targetId, fields),
    onSuccess: (data) =>
      queryClient.setQueryData(mcKeys.settings(targetId), data),
  });
}

/** A run's cost breakdown, refreshed while the run is active. Errors (e.g. 404 from an older MC) hide it. */
export function useMcRunUsageQuery(runId: string, { live }: { live: boolean }) {
  return useQuery({
    queryKey: mcKeys.usage(runId),
    queryFn: () => mcApi.getRunUsage(runId),
    enabled: !!runId,
    staleTime: 0,
    retry: retryUnless404,
    refetchInterval: (query) => {
      if (!live) return false;
      // A 404 means this MC API has no usage endpoint. Other errors are temporary, so keep polling.
      const status = (query.state.error as { status?: number } | null)?.status;
      return status === 404 ? false : MC_POLL_INTERVAL_MS;
    },
  });
}

/** One run with its artifacts, refreshed until it reaches a terminal status. */
export function useMcRunQuery(runId: string) {
  return useQuery({
    queryKey: mcKeys.run(runId),
    queryFn: () => mcApi.getRun(runId),
    enabled: !!runId,
    staleTime: 0,
    retry: retryUnless404,
    refetchInterval: (query) =>
      isActiveRun(query.state.data) ? MC_POLL_INTERVAL_MS : false,
  });
}

/**
 * The run's events, fetched incrementally: each refetch asks only for events after the
 * last cached one. Polls while `live`, and fetches once more when the run stops being live.
 */
export function useMcRunEventsQuery(
  runId: string,
  { live }: { live: boolean },
) {
  const queryClient = useQueryClient();
  const wasLive = useRef(live);

  useEffect(() => {
    if (wasLive.current && !live) {
      queryClient.invalidateQueries({ queryKey: mcKeys.events(runId) });
      queryClient.invalidateQueries({ queryKey: mcKeys.usage(runId) });
    }
    wasLive.current = live;
  }, [live, runId, queryClient]);

  return useQuery({
    queryKey: mcKeys.events(runId),
    queryFn: async (): Promise<McEvent[]> => {
      const cached =
        queryClient.getQueryData<McEvent[]>(mcKeys.events(runId)) ?? [];
      const afterSeq = cached.length ? cached[cached.length - 1].seq : 0;
      const added = await mcApi.getRunEventsAfter(runId, afterSeq);
      return added.length ? [...cached, ...added] : cached;
    },
    enabled: !!runId,
    staleTime: 0,
    refetchInterval: live ? MC_POLL_INTERVAL_MS : false,
  });
}

const artifactUrlQuery = (runId: string, key: string) => ({
  queryKey: mcKeys.artifactUrl(runId, key),
  queryFn: () => mcApi.getArtifactUrl(runId, key),
  staleTime: ARTIFACT_URL_TTL_MS,
  gcTime: ARTIFACT_URL_TTL_MS,
  retry: 1,
});

/** A presigned download link for one artifact. Links expire after an hour, so they're reused for 50 minutes. */
export function useMcArtifactUrlQuery(
  runId: string,
  key: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    ...artifactUrlQuery(runId, key),
    select: (data) => data.url,
    enabled: enabled && !!runId && !!key,
  });
}

/** Resolves an artifact's download link on demand, sharing the cache with `useMcArtifactUrlQuery`. */
export function useFetchMcArtifactUrl(runId: string) {
  const queryClient = useQueryClient();
  return async (key: string): Promise<string> =>
    (await queryClient.fetchQuery(artifactUrlQuery(runId, key))).url;
}

export function useStartMcRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: StartMcRunPayload) => mcApi.startRun(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: mcKeys.runs() }),
  });
}

export function useCancelMcRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => mcApi.cancelRun(runId),
    onSuccess: (run) => {
      queryClient.setQueryData<McRunDetail>(mcKeys.run(run.id), (prev) =>
        prev ? { ...prev, ...run } : prev,
      );
      return queryClient.invalidateQueries({ queryKey: mcKeys.runs() });
    },
  });
}

/** The default target's memory, newest first. */
export function useMcMemoryQuery() {
  return useQuery({
    queryKey: mcKeys.memory(),
    queryFn: () => mcApi.getMemory(),
    select: (data): McMemoryEntry[] => data?.entries ?? [],
    staleTime: 0,
    retry: retryUnless404,
  });
}

const invalidateMemory = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: mcKeys.memory() });

export function useCreateMcMemory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entry: { text: string; why?: string }) =>
      mcApi.createMemory(entry),
    onSuccess: () => invalidateMemory(queryClient),
  });
}

export function useUpdateMcMemory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...patch
    }: {
      id: string;
      text?: string;
      why?: string;
    }) => mcApi.updateMemory(id, patch),
    onSuccess: () => invalidateMemory(queryClient),
  });
}

export function useDeleteMcMemory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => mcApi.deleteMemory(entryId),
    onSuccess: () => invalidateMemory(queryClient),
  });
}

/** Every issue of the default target, most recently seen first. */
export function useMcIssuesQuery() {
  return useQuery({
    queryKey: mcKeys.issues(),
    queryFn: () => mcApi.getIssues(),
    select: (data): McIssue[] => data?.issues ?? [],
    staleTime: 0,
    retry: retryUnless404,
  });
}

/** One issue with every run that reported it. */
export function useMcIssueQuery(issueId: number | null) {
  return useQuery({
    queryKey: mcKeys.issue(issueId ?? 0),
    queryFn: () => mcApi.getIssue(issueId!),
    enabled: issueId !== null,
    staleTime: 0,
    retry: retryUnless404,
  });
}

/** Runs show their findings' issue status, so those refresh too. */
export function useUpdateMcIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...patch
    }: {
      id: number;
      status?: McIssueStatus;
      note?: string;
    }) => mcApi.updateIssue(id, patch),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: mcKeys.issues() }),
        queryClient.invalidateQueries({ queryKey: [...mcKeys.all, "run"] }),
      ]),
  });
}
